/** Client-side fallback sorting for admin grids when server sort is not active. */

import { getLocationFromProvider } from "./addressUtils";
import { getPortalAccessLabelFromSponsor, getPortalAccessLabelFromVendor } from "./roles";
import { sortConfigToSponsorUsersByOrgApiParams } from "./sponsorUsersByOrgGridSort";
import { sortConfigToVendorUserApiParams } from "./vendorAdminGridSort";

function compareAdminValues(aVal, bVal, direction, sortKey) {
  if (aVal == null) return direction === "asc" ? 1 : -1;
  if (bVal == null) return direction === "asc" ? -1 : 1;

  if (sortKey === "is_active" && typeof aVal === "boolean" && typeof bVal === "boolean") {
    if (aVal === bVal) return 0;
    if (direction === "asc") return aVal ? -1 : 1;
    return aVal ? 1 : -1;
  }

  if (typeof aVal === "string" && typeof bVal === "string") {
    return direction === "asc"
      ? aVal.localeCompare(bVal, undefined, { sensitivity: "base" })
      : bVal.localeCompare(aVal, undefined, { sensitivity: "base" });
  }

  if (direction === "asc") return aVal > bVal ? 1 : -1;
  return aVal < bVal ? 1 : -1;
}

export function sortAdminGridItems(items, sortConfig, serverSortParams, resolveValue) {
  if (!items?.length) return [];
  if (serverSortParams?.orderBy) return items;

  const sortKey = Object.keys(sortConfig || {})[0];
  if (!sortKey) return items;

  const { direction } = sortConfig[sortKey];
  const getValue = resolveValue || ((item) => item[sortKey]);

  return [...items].sort((a, b) =>
    compareAdminValues(getValue(a, sortKey), getValue(b, sortKey), direction, sortKey)
  );
}

export function resolveVendorAdminValue(item, sortKey) {
  if (sortKey === "portal_access") {
    return item?.portal_access ?? getPortalAccessLabelFromVendor(item) ?? "";
  }
  if (sortKey === "primary_location") {
    return (
      getLocationFromProvider(
        item?.provider_locations?.find((loc) => loc.location_type === "primary"),
        { short: false, abbvState: true }
      ) || ""
    );
  }
  return item[sortKey];
}

export function resolveSponsorAdminValue(item, sortKey, activeTab) {
  if (sortKey === "primary_location") {
    return getLocationFromProvider(
      { city: item.city, state: item.state, country: item.country },
      { short: false, abbvState: true }
    );
  }
  if (sortKey === "organization") {
    return item.sponsor_name || item.organization_name || item.sponsor_organization_name || "";
  }
  if (sortKey === "portal_access") return getPortalAccessLabelFromSponsor(item);
  if (sortKey === "name" && activeTab === "users") {
    return [item.first_name, item.last_name].filter(Boolean).join(" ");
  }
  if (sortKey === "sponsor_name" && activeTab === "organizations") {
    return item.sponsor_name || "";
  }
  if (sortKey === "created" || sortKey === "created_at") {
    return item.created_at ?? item.created;
  }
  return item[sortKey];
}

export function sortBackendUserItems(items, sortConfig, serverSortParams) {
  return sortAdminGridItems(items, sortConfig, serverSortParams);
}

export function sortSponsorUsersByOrgItems(items, sortConfig, serverSortParams) {
  return sortAdminGridItems(items, sortConfig, serverSortParams);
}

export function sortVendorAdminItems(items, sortConfig, serverSortParams) {
  return sortAdminGridItems(items, sortConfig, serverSortParams, resolveVendorAdminValue);
}

export function sortSponsorAdminItems(items, sortConfig, serverSortParams, activeTab) {
  return sortAdminGridItems(items, sortConfig, serverSortParams, (item, key) =>
    resolveSponsorAdminValue(item, key, activeTab)
  );
}

/** discovery.jsx header sort: asc → desc → clear */
export function nextDiscoverySortState(sortColumn, sortDirection, column) {
  if (sortColumn === column && sortDirection === "asc") {
    return { sortColumn: column, sortDirection: "desc", bumpNonce: true, resetPage: true };
  }
  if (sortColumn === column && sortDirection === "desc") {
    return { sortColumn: null, sortDirection: null, bumpNonce: true, resetPage: true };
  }
  return { sortColumn: column, sortDirection: "asc", bumpNonce: true, resetPage: true };
}

export function applyDiscoverySortClick(sortColumn, sortDirection, column, setters) {
  const next = nextDiscoverySortState(sortColumn, sortDirection, column);
  setters.setSortColumn(next.sortColumn);
  setters.setSortDirection(next.sortDirection);
  if (next.bumpNonce) setters.setSortFetchNonce((n) => n + 1);
  if (next.resetPage) setters.setCurrentPage(1);
  return next;
}

/** Sponsor/vendor admin: toggle asc/desc on same column, else start asc */
export function nextSponsorAdminSortConfig(prev, key) {
  if (Object.keys(prev || {}).length === 1 && prev[key]) {
    return { [key]: { direction: prev[key].direction === "asc" ? "desc" : "asc" } };
  }
  return { [key]: { direction: "asc" } };
}

/** Standard admin grid sort toggle (asc → desc on same key) */
export function nextAdminSortConfig(prev, key) {
  return {
    [key]: { direction: prev?.[key]?.direction === "asc" ? "desc" : "asc" },
  };
}

export function applyAdminSortClick(setSortConfig, setPage, key, nextConfig = nextAdminSortConfig) {
  setSortConfig((prev) => nextConfig(prev, key));
  setPage(1);
}

export function resolveTeamServerSortParams(isVendorView, teamSortConfig) {
  return isVendorView
    ? sortConfigToVendorUserApiParams(teamSortConfig)
    : sortConfigToSponsorUsersByOrgApiParams(teamSortConfig);
}

export function resolveTeamUsersSortOptions(isVendorView, isSponsorView, teamSortConfig) {
  const teamServerSortParams = resolveTeamServerSortParams(isVendorView, teamSortConfig);
  return {
    teamServerSortParams,
    vendorSortOptions: isVendorView ? teamServerSortParams : undefined,
    sponsorSortOptions: isSponsorView ? teamServerSortParams : undefined,
  };
}
